import { Fragment, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Popover, Transition } from "@headlessui/react";
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
  XMarkIcon,
} from "@heroicons/react/20/solid";
import { BellIcon } from "@heroicons/react/24/outline";
import { toast } from "react-toastify";
import {
  getFreshCompanyDocumentDownloadNotificationUrl,
  markCompanyDocumentDownloadNotificationRead,
} from "../api/companyDocuments";
import { useVaultDownloadNotificationStore } from "../store/useVaultDownloadNotificationStore";
import { VaultDownloadNotification } from "../types/companyDocuments";
import {
  CaptableAiNotification,
  isCompanyOnboardingNotification,
} from "../api/companyExcelOnboarding";
import {
  isTerminalCaptableBatch,
  useBackgroundJobsStore,
} from "../store/useBackgroundJobsStore";
import { useStage4NotificationsStore } from "../store/useStage4NotificationsStore";

function relativeTime(value: string) {
  const timestamp = new Date(value).getTime();
  const elapsed = Date.now() - timestamp;
  if (Number.isNaN(timestamp) || elapsed < 60 * 1000) return "Just now";
  const minutes = Math.floor(elapsed / (60 * 1000));
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function openSignedDownload(url: string) {
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.target = "_blank";
  anchor.rel = "noopener noreferrer";
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
}

function NotificationStatusIcon({
  status,
}: {
  status: VaultDownloadNotification["status"];
}) {
  if (status === "READY") {
    return <CheckCircleIcon className="h-5 w-5 text-green-600" />;
  }
  if (status === "FAILED") {
    return <ExclamationCircleIcon className="h-5 w-5 text-red-600" />;
  }
  return <ArrowPathIcon className="h-5 w-5 text-primary animate-spin" />;
}

function CaptableNotificationStatusIcon({ status }: { status: string }) {
  if (status === "COMPLETED") {
    return <CheckCircleIcon className="h-5 w-5 text-green-600" />;
  }
  if (
    status === "COMPLETED_WITH_ERRORS" ||
    status === "ACTION_REQUIRED" ||
    status === "NEEDS_INPUT" ||
    status === "READY_TO_ONBOARD"
  ) {
    return <ExclamationCircleIcon className="h-5 w-5 text-amber-600" />;
  }
  if (status === "PROCESSING") {
    return <ArrowPathIcon className="h-5 w-5 animate-spin text-primary" />;
  }
  return <ExclamationCircleIcon className="h-5 w-5 text-red-600" />;
}

function captableNotificationTitle(notification: CaptableAiNotification) {
  const status = (
    notification.workflowStatus || notification.status
  ).toUpperCase();
  if (status === "ACTION_REQUIRED" || status === "NEEDS_INPUT")
    return "Companies need review";
  if (status === "READY_TO_ONBOARD") return "Companies ready to onboard";
  if (status === "ONBOARDING") return "Companies onboarding";
  if (status === "PROCESSING")
    return isCompanyOnboardingNotification(notification)
      ? "Companies processing"
      : "Cap table processing";
  if (status === "COMPLETED")
    return isCompanyOnboardingNotification(notification)
      ? "Onboarding completed"
      : "Cap table completed";
  if (status === "COMPLETED_WITH_ERRORS")
    return isCompanyOnboardingNotification(notification)
      ? "Onboarding completed with errors"
      : "Cap table completed with errors";
  return isCompanyOnboardingNotification(notification)
    ? "Company onboarding failed"
    : "Cap table failed";
}

const notificationDisplayStatus = (notification: CaptableAiNotification) =>
  (notification.workflowStatus || notification.status).toUpperCase();

export default function FlyoutNotification() {
  const navigate = useNavigate();
  const location = useLocation();
  const [show, setShown] = useState(false);
  const [openingId, setOpeningId] = useState<string | null>(null);
  const notifications = useVaultDownloadNotificationStore(
    (state) => state.notifications
  );
  const markRead = useVaultDownloadNotificationStore((state) => state.markRead);
  const upsertNotification = useVaultDownloadNotificationStore(
    (state) => state.upsertNotification
  );
  const dismissDownloadNotification = useVaultDownloadNotificationStore(
    (state) => state.dismissNotification
  );
  const companyId = useVaultDownloadNotificationStore(
    (state) => state.companyId
  );
  const unreadCount = notifications.filter(
    (notification) => !notification.isRead
  ).length;
  const captableNotifications = useBackgroundJobsStore(
    (state) => state.notifications
  );
  const captableUnreadCount = useBackgroundJobsStore(
    (state) => state.unreadCount
  );
  const markCaptableNotificationRead = useBackgroundJobsStore(
    (state) => state.markRead
  );
  const dismissCaptableNotification = useBackgroundJobsStore(
    (state) => state.dismissNotification
  );
  const dismissAllCaptableNotifications = useBackgroundJobsStore(
    (state) => state.dismissAllNotifications
  );
  const focusBatch = useBackgroundJobsStore((state) => state.focusBatch);
  const captableBatches = useBackgroundJobsStore((state) => state.batches);
  const batchContexts = useBackgroundJobsStore((state) => state.batchContexts);
  const refreshBatch = useBackgroundJobsStore((state) => state.refreshBatch);
  const readProcessingBatches = useBackgroundJobsStore(
    (state) => state.readProcessingBatches
  );
  const markProcessingRead = useBackgroundJobsStore(
    (state) => state.markProcessingRead
  );
  const stage4Notifications = useStage4NotificationsStore(
    (state) => state.notifications
  );
  const markStage4Read = useStage4NotificationsStore((state) => state.markRead);
  const clearStage4Notification = useStage4NotificationsStore(
    (state) => state.clear
  );
  const clearAllStage4Notifications = useStage4NotificationsStore(
    (state) => state.clearAll
  );
  const terminalBatchIds = new Set(
    Object.values(captableNotifications).map(
      (notification) => notification.batchId
    )
  );
  const processingBatches = Object.values(captableBatches).filter(
    (batch) =>
      !isTerminalCaptableBatch(batch) && !terminalBatchIds.has(batch.batchId)
  );
  const totalUnreadCount =
    unreadCount +
    captableUnreadCount +
    stage4Notifications.filter((notification) => !notification.isRead).length +
    processingBatches.filter((batch) => !readProcessingBatches[batch.batchId])
      .length;
  const notificationFeed = [
    ...notifications.map((notification) => ({
      kind: "download" as const,
      notification,
    })),
    ...Object.values(captableNotifications).map((notification) => ({
      kind: "captable" as const,
      notification,
    })),
    ...processingBatches.map((batch) => ({
      kind: "processing" as const,
      notification: {
        id: `processing-${batch.batchId}`,
        batchId: batch.batchId,
        companyId: batch.companyId || null,
        status: "PROCESSING" as const,
        category:
          batchContexts[batch.batchId]?.flow === "company-onboarding"
            ? "AI_COMPANY_ONBOARDING"
            : "CAPTABLE_AI",
        workflowStatus: "PROCESSING",
        message: `${
          batch.files?.filter((file) => file.status === "COMPLETED").length || 0
        }/${batch.files?.length || 0} files completed`,
        isRead: Boolean(readProcessingBatches[batch.batchId]),
        createdAt: batch.createdAt || batch.updatedAt || "",
      },
    })),
  ].sort(
    (first, second) =>
      new Date(second.notification.createdAt).getTime() -
      new Date(first.notification.createdAt).getTime()
  );

  const handleNotification = async (
    notification: VaultDownloadNotification
  ) => {
    if (!notification.isRead) {
      markRead(notification.id);
      try {
        await markCompanyDocumentDownloadNotificationRead(notification.id);
      } catch (_error) {
        // The optimistic state only controls a visual badge. The durable list
        // is rehydrated whenever this session reconnects.
      }
    }

    if (notification.status !== "READY") return;
    setOpeningId(notification.id);
    try {
      const refreshedNotification =
        await getFreshCompanyDocumentDownloadNotificationUrl(notification.id);
      if (companyId) upsertNotification(companyId, refreshedNotification);
      if (!refreshedNotification.downloadUrl) {
        throw new Error("This download link is no longer available.");
      }
      // Do not append cache-busting query params to a signed S3 URL.
      openSignedDownload(refreshedNotification.downloadUrl);
    } catch (error) {
      toast(
        error instanceof Error
          ? error.message
          : "This download link is no longer available.",
        { type: "error", autoClose: 3000 }
      );
    } finally {
      setOpeningId(null);
    }
  };

  const openCaptableBatch = (batchId: string) => {
    focusBatch(batchId);
    setShown(false);
    navigate("/setting/captable-ai/jobs", {
      state: { batchId, returnTo: location.pathname },
    });
    refreshBatch(batchId).catch(() => undefined);
  };

  const openNotificationBatch = (notification: CaptableAiNotification) => {
    if (
      batchContexts[notification.batchId]?.flow === "company-onboarding" ||
      isCompanyOnboardingNotification(notification)
    ) {
      focusBatch(notification.batchId);
      setShown(false);
      navigate(`/company-onboarding/batches/${notification.batchId}`);
      return;
    }
    openCaptableBatch(notification.batchId);
  };

  const handleCaptableNotification = (notification: CaptableAiNotification) => {
    openNotificationBatch(notification);
    if (!notification.isRead) {
      // Navigation must never be blocked by a temporary read API failure. The
      // store still changes to read only after this request succeeds.
      markCaptableNotificationRead(notification.id).catch(() => undefined);
    }
  };

  const clearDownloadNotification = async (notificationId: string) => {
    await markCompanyDocumentDownloadNotificationRead(notificationId);
    dismissDownloadNotification(notificationId);
  };

  return (
    <Popover className="relative">
      <Popover.Button
        onClick={() => setShown(!show)}
        onMouseEnter={() => {
          setShown(true);
        }}
        onMouseLeave={() => {
          setShown(false);
        }}
        className="inline-flex h-9 w-9 items-center justify-center rounded-md text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary/30"
      >
        <span className="sr-only">View notifications</span>
        <BellIcon className="h-5 w-5" strokeWidth={2} aria-hidden="true" />
        {totalUnreadCount > 0 && (
          <span className="absolute -right-1 -top-1 min-w-[1.1rem] rounded-full bg-primary px-1 text-center text-[10px] font-semibold leading-[1.1rem] text-white">
            {totalUnreadCount > 9 ? "9+" : totalUnreadCount}
          </span>
        )}
      </Popover.Button>

      <Transition
        show={show}
        as={Fragment}
        enter="transition ease-out duration-200"
        enterFrom="opacity-0 translate-y-1"
        enterTo="opacity-100 translate-y-0"
        leave="transition ease-in duration-150"
        leaveFrom="opacity-100 translate-y-0"
        leaveTo="opacity-0 translate-y-1"
      >
        <Popover.Panel
          onMouseEnter={() => {
            setShown(true);
          }}
          onMouseLeave={() => {
            setShown(false);
          }}
          className="absolute z-10 mt-5 flex w-screen max-w-max  -right-8 "
        >
          <div className="w-screen max-w-md flex-auto overflow-hidden rounded-xl bg-white text-sm leading-6 shadow-lg border border-gray-100">
            <div className="flex items-center justify-between border-b border-gray-100 bg-gray-50 px-4 py-3">
              <span className="font-semibold text-gray-900">Notifications</span>
              <span className="flex items-center gap-3">
                {totalUnreadCount > 0 && (
                  <span className="text-xs text-gray-500">
                    {totalUnreadCount} new
                  </span>
                )}
                {(Object.keys(captableNotifications).length > 0 ||
                  stage4Notifications.length > 0) && (
                  <button
                    type="button"
                    className="text-xs font-medium text-primary hover:underline"
                    onClick={() => {
                      Promise.all([
                        dismissAllCaptableNotifications(false),
                        clearAllStage4Notifications(),
                      ]).catch(() => undefined);
                    }}
                  >
                    Clear all
                  </button>
                )}
              </span>
            </div>
            <div className="max-h-96 overflow-y-auto p-2">
              {stage4Notifications.map((notification) => (
                <div
                  key={`stage4-${notification.id}`}
                  className="group relative"
                >
                  <button
                    type="button"
                    onClick={() => {
                      if (!notification.isRead)
                        markStage4Read(notification.id).catch(() => undefined);
                      setShown(false);
                      navigate("/setting/external-captable");
                    }}
                    className={`flex w-full gap-3 rounded-lg px-3 py-3 pr-10 text-left transition hover:bg-gray-50 ${
                      notification.isRead ? "" : "bg-primary/[0.04]"
                    }`}
                  >
                    <span className="mt-0.5 shrink-0">
                      {/(failed|error)/i.test(
                        `${notification.status || ""} ${
                          notification.message || ""
                        }`
                      ) ? (
                        <XCircleIcon className="h-5 w-5 text-red-600" />
                      ) : (
                        <CheckCircleIcon className="h-5 w-5 text-green-600" />
                      )}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-start justify-between gap-3">
                        <span className="truncate font-medium text-gray-900">
                          {notification.type === "workbook"
                            ? notification.filename || "Stage-4 workbook"
                            : "Stage-4 batch"}
                        </span>
                        {notification.createdAt && (
                          <time className="shrink-0 text-xs text-gray-400">
                            {relativeTime(notification.createdAt)}
                          </time>
                        )}
                      </span>
                      <span className="mt-0.5 block text-xs leading-5 text-gray-500">
                        {notification.message}
                      </span>
                    </span>
                  </button>
                  <button
                    type="button"
                    aria-label="Clear Stage-4 notification"
                    className="absolute right-2 top-2 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                    onClick={() =>
                      clearStage4Notification(notification.id).catch(
                        () => undefined
                      )
                    }
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </div>
              ))}
              {notificationFeed.length === 0 &&
              stage4Notifications.length === 0 ? (
                <div className="px-3 py-8 text-center text-sm text-gray-500">
                  Your notifications will appear here.
                </div>
              ) : (
                notificationFeed.map((item) =>
                  item.kind === "download" ? (
                    <div
                      key={`download-${item.notification.id}`}
                      className="group relative"
                    >
                      <button
                        type="button"
                        onClick={() => {
                          handleNotification(item.notification).catch(
                            () => undefined
                          );
                        }}
                        disabled={openingId === item.notification.id}
                        className={`flex w-full gap-3 rounded-lg px-3 py-3 pr-10 text-left transition hover:bg-gray-50 disabled:cursor-wait ${
                          item.notification.isRead ? "" : "bg-primary/[0.04]"
                        }`}
                      >
                        <span className="mt-0.5 shrink-0">
                          <NotificationStatusIcon
                            status={item.notification.status}
                          />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="flex items-start justify-between gap-3">
                            <span className="truncate font-medium text-gray-900">
                              {item.notification.title}
                            </span>
                            <time className="shrink-0 text-xs text-gray-400">
                              {relativeTime(item.notification.createdAt)}
                            </time>
                          </span>
                          <span className="mt-0.5 block text-xs leading-5 text-gray-500">
                            {item.notification.message}
                          </span>
                          {item.notification.status === "READY" && (
                            <span className="mt-1 block text-xs font-medium text-primary">
                              {openingId === item.notification.id
                                ? "Opening download…"
                                : "Download ZIP"}
                            </span>
                          )}
                        </span>
                      </button>
                      <button
                        type="button"
                        aria-label="Clear notification"
                        className="absolute right-2 top-2 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                        onClick={() => {
                          clearDownloadNotification(item.notification.id).catch(
                            () => undefined
                          );
                        }}
                      >
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <div
                      key={`captable-${item.notification.id}`}
                      className="group relative"
                    >
                      <button
                        type="button"
                        onClick={() => {
                          if (item.kind === "processing") {
                            markProcessingRead(item.notification.batchId);
                            if (
                              batchContexts[item.notification.batchId]?.flow ===
                              "company-onboarding"
                            ) {
                              setShown(false);
                              navigate(
                                `/company-onboarding/batches/${item.notification.batchId}`
                              );
                            } else {
                              openCaptableBatch(item.notification.batchId);
                            }
                          } else {
                            handleCaptableNotification(item.notification);
                          }
                        }}
                        className={`flex w-full gap-3 rounded-lg px-3 py-3 pr-10 text-left transition hover:bg-gray-50 ${
                          ["ACTION_REQUIRED", "NEEDS_INPUT"].includes(
                            notificationDisplayStatus(item.notification)
                          )
                            ? "bg-amber-50/70"
                            : item.notification.isRead
                            ? ""
                            : "bg-primary/[0.04]"
                        }`}
                      >
                        <span className="mt-0.5 shrink-0">
                          <CaptableNotificationStatusIcon
                            status={notificationDisplayStatus(
                              item.notification
                            )}
                          />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="flex items-start justify-between gap-3">
                            <span className="font-medium text-gray-900">
                              {captableNotificationTitle(item.notification)}
                            </span>
                            <time className="shrink-0 text-xs text-gray-400">
                              {relativeTime(item.notification.createdAt)}
                            </time>
                          </span>
                          <span className="mt-0.5 block text-xs leading-5 text-gray-500">
                            {item.notification.message}
                          </span>
                          <span className="mt-1 block text-xs font-medium text-primary">
                            {["ACTION_REQUIRED", "NEEDS_INPUT"].includes(
                              notificationDisplayStatus(item.notification)
                            )
                              ? "Review details"
                              : item.notification.companyId
                              ? `View company ${item.notification.companyId}`
                              : "View onboarding request"}
                          </span>
                        </span>
                      </button>
                      {item.kind === "captable" && item.notification.isRead && (
                        <button
                          type="button"
                          aria-label="Clear notification"
                          className="absolute right-2 top-2 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                          onClick={() => {
                            dismissCaptableNotification(
                              item.notification.id,
                              false
                            ).catch(() => undefined);
                          }}
                        >
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  )
                )
              )}
            </div>
          </div>
        </Popover.Panel>
      </Transition>
    </Popover>
  );
}
